import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FacilityCoreFilterBar from '../shared/FacilityCoreFilterBar';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import { useCountUp } from '../../../hooks/useCountUp';
import { useCollectionSync } from '../../../hooks/useCollectionSync';
import { useAuth } from '../../../context/AuthContext';
import { useSync } from '../../../context/SyncContext';
import { useNotifications } from '../../../context/NotificationContext';
import { facilitiesApi, normalizeFacility } from '../../../api/facilitiesApi';
import { marketsApi } from '../../../api/marketsApi';
import {
  ACT, functionalBadgeHtml, licensedBadgeHtml, dataStatusBadgeHtml,
  ROLE_CAN, ROLE_WORKFLOW_TRANSITIONS, EDITABLE_STATUSES,
} from '../shared/FacilityWorkflow';
import {
  ActionDropdown, anchorRect, RejectNotesModal, DeleteConfirmModal, QuickActionOverlay, WORKFLOW_RULES,
} from '../../shared/RowActionMenu';
import FacilityMap from '../shared/FacilityMap';
import { deriveFacilityClimateStatuses } from '../../../api/facilityStatusUpdatesApi';
import {
  CSA_PRACTICE_OPTIONS, MANURE_SYSTEM_OPTIONS, PRACTICE_VERIFICATION_OPTIONS, RECOVERY_STATUS_OPTIONS,
  WASTE_APPROVAL_OPTIONS, climateSectionFor, labelOf,
} from '../shared/climateOptions';
import { normalizeGpsPoint } from '../../../services/gpsCoordinates';

//  Stat Cards 
// DCIC-06 card mapping:
//   facility_type-based cards  GET /v1/facilities/counts?status=approved
//   Markets  DCIC-02 Module 2 Market Registry (marketsApi), single source of truth
//   Processing Plant  D-008 blocked (facility_type not yet in ENUM)
const CARD_DEFS = [
  { label: 'Total Abattoirs',          sub: 'Abattoirs Nationwide',        source: 'facility', types: ['abattoir', 'slaughter_slab'] },
  { label: 'Total Markets',            sub: 'Markets Nationwide',          source: 'markets' },
  { label: 'Total Veterinary Clinics', sub: 'Vet Clinics Nationwide',      source: 'facility', types: ['veterinary_clinic', 'veterinary_hospital'] },
  { label: 'Total Livestock Farms',    sub: 'Livestock Farms Registered',  source: 'facility', types: ['livestock_farm'] },
  { label: 'Total Biogas Systems',     sub: 'Biogas Digesters Registered', source: 'facility', types: ['biogas_system'] },
  { label: 'Total Grazing Reserves',   sub: 'Grazing Reserves Nationwide', source: 'facility', types: ['grazing_reserve'] },
  { label: 'Total Feed Mills',         sub: 'Feed Mills Nationwide',       source: 'facility', types: ['feed_mill', 'feedmill'] },
];

function StatCard({ label, value, sub, blocked }) {
  const end = blocked ? 0 : (parseInt(String(value).replace(/,/g, ''), 10) || 0);
  const { count, ref } = useCountUp(end);
  return (
    <div ref={ref} className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="text-[11px] text-gray-500 leading-tight mb-1">{label}</p>
      <p className="text-2xl font-black text-gray-900">{blocked ? '' : count.toLocaleString()}</p>
      <p className="text-[10px] text-gray-400 mt-1 leading-tight">{sub}</p>
    </div>
  );
}

function StatCards() {
  const [facilityCounts, setFacilityCounts] = useState({});
  const [marketTotal,    setMarketTotal]    = useState(0);
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    Promise.all([
      facilitiesApi.counts({ status: 'approved' }),
      marketsApi.list({ per_page: 1 }),
    ])
      .then(([counts, marketPage]) => {
        setFacilityCounts(counts);
        setMarketTotal(
          marketPage.pagination?.total ??
          marketPage.pagination?.count ??
          marketPage.pagination?.total_records ??
          0
        );
      })
      .catch(() => { setFacilityCounts({}); setMarketTotal(0); })
      .finally(() => setLoading(false));
  }, []);

  const getValue = (def) => {
    if (def.source === 'blocked') return { blocked: true,  value: 0 };
    if (def.source === 'markets') return { blocked: false, value: marketTotal };
    const val = (def.types || []).reduce((sum, t) => sum + (facilityCounts[t] ?? 0), 0);
    return { blocked: false, value: val };
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
      {CARD_DEFS.map((def, i) => {
        const { blocked, value } = getValue(def);
        return (
          <StatCard
            key={i}
            label={def.label}
            value={loading ? 0 : value}
            sub={def.sub}
            blocked={blocked}
          />
        );
      })}
    </div>
  );
}

// Capacity details recorded for the facility type (dictionary: capacity_fields).
const CAPACITY_LABELS = [
  ['daily_slaughter_capacity', (v) => `${v} head/day`],
  ['annual_production_capacity', (v) => `${v} t/yr`],
  ['cold_storage_capacity', (v) => `${v} t`],
  ['cold_storage_type', (v) => v],
  ['daily_milk_capacity_litres', (v) => `${v} L/day`],
  ['water_source', (v) => v],
];
function capacitySummary(row) {
  const meta = row?.capacity_metadata && typeof row.capacity_metadata === 'object' ? row.capacity_metadata : {};
  const parts = CAPACITY_LABELS.filter(([key]) => meta[key] !== undefined && meta[key] !== '' && meta[key] !== null).map(([key, fmt]) => fmt(meta[key]));
  if (row?.biogas?.digester_volume_m3) parts.push(`${row.biogas.digester_volume_m3} m³ digester`);
  return parts.join(' · ');
}

// v2.2 climate summary — the key values from the facility's climate section,
// with the system-set verification status.
function climateSummary(row) {
  const section = climateSectionFor(row?.facility_type);
  if (section === 'climate') {
    const c = row.climate ?? {};
    const practices = (c.csa_practices_adopted ?? []).map((v) => labelOf(CSA_PRACTICE_OPTIONS, v)).join(', ');
    const status = labelOf(PRACTICE_VERIFICATION_OPTIONS, deriveFacilityClimateStatuses(row.uuid).practice_verification_status);
    return [practices && `CSA: ${practices}`, c.manure_management_system && `Manure: ${labelOf(MANURE_SYSTEM_OPTIONS, c.manure_management_system)}`, `Verification: ${status}`].filter(Boolean).join(' · ');
  }
  if (section === 'waste_recovery') {
    const w = row.waste_recovery ?? {};
    if (!(w.waste_recovery_system ?? []).length) return 'Waste recovery not recorded';
    const status = w.recovery_status ? labelOf(RECOVERY_STATUS_OPTIONS, w.recovery_status) : 'None';
    const approval = labelOf(WASTE_APPROVAL_OPTIONS, deriveFacilityClimateStatuses(row.uuid).approval_status);
    return `Recovery: ${status} · Approval: ${approval}`;
  }
  if (section === 'biogas') {
    const b = row.biogas ?? {};
    return b.commissioning_date ? `Commissioned ${b.commissioning_date}` : 'Biogas details not recorded';
  }
  return '';
}
const summaryCell = (fn) => (_v, type, row) => {
  const text = fn(row);
  if (type !== 'display') return text;
  return text ? `<span style="font-size:12px;color:#374151">${text}</span>` : '<span style="color:#9ca3af">—</span>';
};

const COLUMNS = [
  { title: 'Name',              data: 'name',             sortable: true },
  { title: 'State',             data: 'state',            sortable: true },
  { title: 'LGA',               data: 'lga',              sortable: true },
  { title: 'Ward',              data: 'ward',             sortable: true, defaultContent: '' },
  { title: 'GPS Coordinates',   data: 'gps_coordinates',  sortable: true },
  { title: 'Facility Type',     data: 'facilityType',     sortable: true },
  { title: 'Ownership',         data: 'ownership',        sortable: true },
  { title: 'Licensed Status',   data: 'licensedStatus',   sortable: true, render: licensedBadgeHtml },
  { title: 'Capacity',          data: null,               sortable: false, render: summaryCell(capacitySummary) },
  { title: 'Climate',           data: null,               sortable: false, render: summaryCell(climateSummary) },
  { title: 'Functional Status', data: 'functionalStatus', sortable: true, render: functionalBadgeHtml },
  { title: 'Data Status',       data: 'validationStatus', sortable: true, render: dataStatusBadgeHtml },
  { title: 'Action',            data: null,               sortable: false, render: ACT },
];

function normalizeForCompare(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// Client-side filtering, mirroring the dropdown filter set — the backend
// doesn't reliably honor these query params, so filters are applied here
// against the already-loaded records instead (same approach Conflicts &
// Dispute Records uses).
function filterFacilityRows(rows, filters = {}) {
  const state = normalizeForCompare(filters.state);
  const lga = normalizeForCompare(filters.lga);
  const facilityType = normalizeForCompare(filters.facilityType);
  const functionalStatus = normalizeForCompare(filters.functionalStatus);
  const ownership = normalizeForCompare(filters.ownership);
  const validationStatus = normalizeForCompare(filters.validationStatus);

  return rows.filter((row) => {
    if (state && normalizeForCompare(row.state) !== state) return false;
    if (lga && normalizeForCompare(row.lga) !== lga) return false;
    if (facilityType && normalizeForCompare(row.facility_type) !== facilityType) return false;
    if (functionalStatus && normalizeForCompare(row.functional_status) !== functionalStatus) return false;
    if (ownership && normalizeForCompare(row.ownership_raw) !== ownership) return false;
    if (validationStatus) {
      const rowDataStatus = normalizeForCompare(row.data_status);
      const rowValidationStatus = normalizeForCompare(row.validation_status);
      if (rowDataStatus !== validationStatus && rowValidationStatus !== validationStatus) return false;
    }
    return true;
  });
}

//  Main component 
export default function FacilityCoreRegistry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshNotifications } = useSync();
  const { pushWorkflowNotification } = useNotifications();
  const roleId = user?.roleId ?? '';
  const isR05 = roleId === 'R05';
  const canPrimaryAction = roleId === 'R01' || roleId === 'R04' || roleId === 'R05';
  const primaryActionLabel = 'Create New';
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [filters,     setFilters]     = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [actionMenu,  setActionMenu]  = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quickBusy,    setQuickBusy]    = useState('');
  const [mapSelected,  setMapSelected]  = useState(null);
  const requestRef = useRef(0);

  const filteredRows = useMemo(() => filterFacilityRows(rows, filters), [rows, filters]);

  const mapMarkers = useMemo(() => filteredRows.map((row) => {
    const point = normalizeGpsPoint(row.gps_coordinates);
    if (!point) return null;
    return {
      id: row.uuid ?? row.id,
      lat: point.lat,
      lng: point.lng,
      name: row.name || 'Unnamed facility',
      status: row.functional_status || row.functionalStatus,
    };
  }).filter(Boolean), [filteredRows]);

  const loadData = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const { items } = await facilitiesApi.list({ per_page: 1000 });
      if (requestId !== requestRef.current) return;
      setRows(items);
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to load facilities.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const reloadRecords = useCallback(() => loadData(), [loadData]);
  useCollectionSync(facilitiesApi.collectionKey, reloadRecords);

  const handleFiltersChange = useCallback((newFilters) => setFilters(newFilters), []);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'view' || action === 'menu') setActionMenu({ row, rect });
  }, []);

  const patchRow = useCallback((uuid, patch = {}) => {
    setRows((prev) => prev.map((row) => (row.uuid === uuid ? normalizeFacility({ ...row, ...patch }) : row)));
  }, []);

  const handleEditSave = useCallback(async (uuid, payload) => {
    const currentRow = rows.find((row) => (row.uuid ?? row.id) === uuid) ?? null;
    const currentStatus = String(
      currentRow?.data_status ??
      currentRow?.validationStatus ??
      currentRow?.validation_status ??
      currentRow?.workflow_status ??
      'draft'
    ).toLowerCase();
    const requestedStatus = String(payload?.data_status ?? currentStatus).toLowerCase();
    const basePayload = { ...payload };
    delete basePayload.data_status;
    const rejectionNotes = basePayload.rejection_notes;
    const transitioningToReject = requestedStatus !== currentStatus && requestedStatus === 'rejected';
    if (transitioningToReject) {
      delete basePayload.rejection_notes;
    }

    let updated = null;
    if (Object.keys(basePayload).length) {
      updated = await facilitiesApi.update(uuid, basePayload);
    }
    if (requestedStatus !== currentStatus) {
      if (requestedStatus === 'submitted') {
        updated = await facilitiesApi.submit(uuid);
      } else if (requestedStatus === 'validated') {
        updated = await facilitiesApi.validate(uuid);
      } else if (requestedStatus === 'approved') {
        updated = await facilitiesApi.approve(uuid);
      } else if (requestedStatus === 'rejected') {
        updated = await facilitiesApi.reject(uuid, rejectionNotes ?? '');
      }
      if (['submitted', 'validated', 'approved', 'rejected'].includes(requestedStatus)) {
        pushWorkflowNotification({
          moduleKey: 'infrastructure', roleCan: ROLE_CAN, newStatus: requestedStatus,
          actorRoleId: roleId, recordLabel: currentRow?.name, notes: rejectionNotes,
        });
      }
      // Re-fetch notifications so the actor sees any backend-generated alerts immediately
      refreshNotifications();
    }
    patchRow(uuid, { ...basePayload, ...(updated ?? {}), data_status: updated?.data_status ?? requestedStatus });
    return updated;
  }, [patchRow, rows, refreshNotifications, pushWorkflowNotification, roleId]);

  const handleQuickStatusSave = useCallback(async (row, status, notes = '') => {
    setError('');
    setQuickBusy(
      status === 'submitted' ? 'Submitting...' :
      status === 'validated' ? 'Validating...' :
      status === 'approved' ? 'Approving...' :
      status === 'rejected' ? 'Rejecting...' :
      'Saving...'
    );
    try {
      await handleEditSave(row.uuid ?? row.id, {
        data_status: status,
        ...(status === 'rejected' ? { rejection_notes: notes } : {}),
      });
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? `Failed to update ${status}.`);
    } finally {
      setQuickBusy('');
    }
  }, [handleEditSave, loadData]);

  const handleQuickSubmit = useCallback(async (row) => {
    await handleQuickStatusSave(row, 'submitted');
  }, [handleQuickStatusSave]);

  const handleQuickValidate = useCallback(async (row) => {
    await handleQuickStatusSave(row, 'validated');
  }, [handleQuickStatusSave]);

  const handleQuickApprove = useCallback(async (row) => {
    await handleQuickStatusSave(row, 'approved');
  }, [handleQuickStatusSave]);

  const handleRejectConfirm = useCallback(async (notes) => {
    if (!rejectTarget) return;
    await handleQuickStatusSave(rejectTarget, 'rejected', notes);
    setRejectTarget(null);
  }, [handleQuickStatusSave, rejectTarget]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setError('');
    setQuickBusy('Deleting...');
    try {
      await facilitiesApi.remove(deleteTarget.uuid ?? deleteTarget.id);
      setDeleteTarget(null);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to delete draft.');
    } finally {
      setQuickBusy('');
    }
  }, [deleteTarget, loadData]);

  return (
    <div>
      <FacilityCoreFilterBar
        canCreate={canPrimaryAction}
        actionLabel={primaryActionLabel}
        onSearch={setSearchQuery}
        onFiltersChange={handleFiltersChange}
        onAddRecord={() => navigate('/infrastructure/new?subModule=core')}
        hideFilters={isR05}
      />

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 mb-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => loadData()}
            className="ml-4 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500 mb-3">
          Loading facilities...
        </div>
      )}

      {!isR05 && <StatCards />}

      <NlimsDataTable
        tableId="facility-core-table"
        columns={COLUMNS}
        data={filteredRows}
        defaultSort={[1, 'asc']}
        searchQuery={searchQuery}
        hideSearch
        pageLength={25}
        onRowAction={handleRowAction}
      />

      <div className="mt-6">
        <p className="mb-3 text-sm font-semibold text-gray-700">Facility Map View</p>
        <FacilityMap markers={mapMarkers} selected={mapSelected} onSelect={setMapSelected} />
      </div>

      {actionMenu && !quickBusy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.facilities}
          onClose={() => setActionMenu(null)}
          onView={() => { const r = actionMenu.row; setActionMenu(null); navigate(`/infrastructure/view/${r.uuid}?subModule=core`, { state: { row: r } }); }}
          onEdit={() => { const r = actionMenu.row; setActionMenu(null); navigate(`/infrastructure/edit/${r.uuid}?subModule=core`, { state: { row: r } }); }}
          onSubmit={() => { const r = actionMenu.row; setActionMenu(null); handleQuickSubmit(r); }}
          onValidate={() => { const r = actionMenu.row; setActionMenu(null); handleQuickValidate(r); }}
          onApprove={() => { const r = actionMenu.row; setActionMenu(null); handleQuickApprove(r); }}
          onReject={() => { setRejectTarget(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}

      {rejectTarget && (
        <RejectNotesModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          row={deleteTarget}
          entityLabel={(r) => `${r?.name ?? ''}${r?.lga ? ` - ${r.lga}` : ''}`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={!!quickBusy}
        />
      )}

      {quickBusy && <QuickActionOverlay label={quickBusy} />}
    </div>
  );
}

